import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import dayjs, { Dayjs } from "dayjs";

interface DateRangeState {
  startDate: Dayjs | null;
  endDate: Dayjs | null;

  // Actions
  setDateRange: (startDate: Dayjs, endDate: Dayjs) => void;
  setStartDate: (startDate: Dayjs) => void;
  setEndDate: (endDate: Dayjs) => void;
  resetDateRange: () => void;
}

export const useDateRangeStore = create<DateRangeState>()(
  devtools(
    persist(
      (set) => ({
        startDate: null,
        endDate: null,

        setDateRange: (startDate, endDate) => {
          set(
            {
              startDate,
              endDate,
            },
            false,
            "setDateRange"
          );
        },

        setStartDate: (startDate) => {
          set(
            {
              startDate,
            },
            false,
            "setStartDate"
          );
        },

        setEndDate: (endDate) => {
          set(
            {
              endDate,
            },
            false,
            "setEndDate"
          );
        },

        resetDateRange: () => {
          set(
            {
              startDate: null,
              endDate: null,
            },
            false,
            "resetDateRange"
          );
        },
      }),
      {
        name: "reports-date-range",
        storage: {
          getItem: (name) => {
            const str = localStorage.getItem(name);
            if (!str) return null;
            const { state } = JSON.parse(str);
            return {
              state: {
                ...state,
                startDate: state.startDate ? dayjs(state.startDate) : null,
                endDate: state.endDate ? dayjs(state.endDate) : null,
              },
            };
          },
          setItem: (name, value) => {
            const str = JSON.stringify({
              state: {
                ...value.state,
                startDate: value.state.startDate?.toISOString() || null,
                endDate: value.state.endDate?.toISOString() || null,
              },
            });
            localStorage.setItem(name, str);
          },
          removeItem: (name) => localStorage.removeItem(name),
        },
      }
    ),
    { name: "DateRangeStore" }
  )
);

// Selectors for optimized re-renders
export const selectStartDate = (state: DateRangeState) => state.startDate;
export const selectEndDate = (state: DateRangeState) => state.endDate;
export const selectDateRange = (state: DateRangeState) => ({
  startDate: state.startDate,
  endDate: state.endDate,
});
export const selectActions = (state: DateRangeState) => ({
  setDateRange: state.setDateRange,
  setStartDate: state.setStartDate,
  setEndDate: state.setEndDate,
  resetDateRange: state.resetDateRange,
});
